import React, { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import EmptySpecificationsPng from 'images/v5/empty-states/specifications.png';
import PlusSvg from 'images/v5/icons/plus.svg';
import Bar from 'layout/atoms/Bar';
import Button from 'layout/atoms/Button/Button';
import InfiniteScroll from 'layout/atoms/InfiniteScroll';
import Label from 'layout/atoms/Label';
import Loader from 'layout/atoms/Loader/Loader';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import FolderCard from 'layout/molecules/FolderCard';
import SpecificationCard from 'layout/molecules/SpecificationCard';
import Tools from 'layout/organisms/Tools';
import Grid from 'molecules/Grid/Grid';
import { openModal } from 'store/layoutSlice';
import { addSpecification } from 'store/specificationDetailsSlice';
import { fetchSpecificationsFolders, resetSpecificationsFolders, } from 'store/specificationsFoldersSlice';
import { addToast } from 'store/toastsSlice';
function FolderView({ link, orderSelect = false, warehouseReleaseSelect = false, folderId: folderIdProp, }) {
    const dispatch = useDispatch();
    const params = useParams();
    const folderId = (folderIdProp !== null && folderIdProp !== void 0 ? folderIdProp : params.folderId);
    const navigate = useNavigate();
    const layout = useSelector((state) => state.layout.specificationsDashboard);
    const { t } = useTranslation(['specification', 'exceptions', 'emptyStates']);
    const folders = useSelector((state) => state.specificationsFolders.folders);
    const specifications = useSelector((state) => state.specificationsFolders.specifications);
    const filters = useSelector((state) => state.specificationsFolders.filters);
    const pageInfo = useSelector((state) => state.specificationsFolders.pageInfo);
    const status = useSelector((state) => state.specificationsFolders.status.fetch);
    const loadedFolderId = useSelector((state) => state.specificationsFolders.loadedFolderId);
    const isCurrentFolderLoaded = loadedFolderId === folderId;
    const refetch = useSelector((state) => state.specificationsFolders.refetchFlags.fetch);
    const error = useSelector((state) => state.specificationsFolders.error);
    const hasActiveFilters = filters.search !== '' || filters.statusFilter.length > 0 || filters.categoryFilter.length > 0;
    const productionSpecifications = specifications.filter((spec) => spec.state === 'PRODUCTION');
    const displayedSpecifications = orderSelect || warehouseReleaseSelect ? productionSpecifications : specifications;
    const stateFilter = orderSelect || warehouseReleaseSelect ? 'PRODUCTION' : null;
    const clearAndFetchSpecifications = useCallback(() => {
        dispatch(resetSpecificationsFolders());
        dispatch(fetchSpecificationsFolders({
            folderId,
            states: stateFilter ? [stateFilter] : null,
        }))
            .unwrap()
            .catch(() => {
            dispatch(addToast({
                theme: 'ghost',
                color: 'negative',
                message: 'Wiadomość',
                title: t('emptyStates:FOLDER_404_HEADER'),
                description: t('emptyStates:FOLDER_404_DESCRIPTION'),
            }));
            navigate('/specifications');
        });
    }, [t, dispatch, navigate, folderId, stateFilter]);
    const fetchMoreSpecifications = () => {
        if (pageInfo.hasNextPage && status !== 'loading') {
            dispatch(fetchSpecificationsFolders({
                folderId,
                append: true,
                states: stateFilter ? [stateFilter] : null,
            }));
        }
    };
    useEffect(() => {
        if (status === 'idle' || refetch) {
            clearAndFetchSpecifications();
        }
    }, [status, refetch, clearAndFetchSpecifications]);
    useEffect(() => {
        if (filters) {
            clearAndFetchSpecifications();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, filters, folderId]);
    const handleAddSpecification = () => {
        dispatch(addSpecification({ folderId })).then((response) => {
            var _a, _b;
            return ((_a = response.payload) === null || _a === void 0 ? void 0 : _a.id) &&
                dispatch(openModal({ name: 'specificationCreator', data: { id: (_b = response.payload) === null || _b === void 0 ? void 0 : _b.id } }));
        });
    };
    if (status === 'idle' ||
        !isCurrentFolderLoaded ||
        (status === 'loading' && folders.length === 0 && specifications.length === 0)) {
        return React.createElement(Loader, null);
    }
    if (status === 'succeeded' &&
        folders.length === 0 &&
        displayedSpecifications.length === 0 &&
        hasActiveFilters) {
        return (React.createElement(EmptyState, { header: t('common:no_results_header'), subheader: t('common:no_results_subheader') }));
    }
    if (status === 'succeeded' && folders.length === 0 && displayedSpecifications.length === 0) {
        return (React.createElement(EmptyState, { icon: React.createElement("img", { src: EmptySpecificationsPng, alt: "" }), header: t('emptyStates:SPECIFICATIONS_HEADER'), subheader: t('emptyStates:SPECIFICATIONS_SUBHEADER') },
            React.createElement(Button, { leftIcon: React.createElement(PlusSvg, null), onClick: handleAddSpecification }, t('emptyStates:SPECIFICATIONS_ADD_BUTTON'))));
    }
    if (status === 'failed') {
        return React.createElement("div", null,
            "Error: ",
            error.fetch);
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Grid, { columns: layout === 'grid' ? 'folder' : 1, gap: 12 }, folders === null || folders === void 0 ? void 0 : folders.map((folder) => (React.createElement(FolderCard, { key: folder.id, folder: folder, layout: layout, link: link })))),
        displayedSpecifications.length > 0 && (React.createElement(Bar, { height: 32 },
            React.createElement(Label, { weight: "thin" }, t('specification_capitalize_other')))),
        React.createElement(InfiniteScroll, { dataLength: displayedSpecifications.length, next: fetchMoreSpecifications, hasMore: pageInfo.hasNextPage, loader: React.createElement(Loader, null), scrollableTarget: "mainContent" },
            React.createElement(Grid, { columns: layout === 'grid' ? 'flexible' : 1, gap: 16 }, displayedSpecifications === null || displayedSpecifications === void 0 ? void 0 : displayedSpecifications.map((specification) => (React.createElement(SpecificationCard, { key: specification.id, specification: specification, layout: layout, orderSelect: orderSelect, warehouseReleaseSelect: warehouseReleaseSelect }))))),
        !warehouseReleaseSelect && !orderSelect && React.createElement(Tools, null)));
}
export default memo(FolderView);
